/**
 * UnsavedChangesModal - Modal for warning about unsaved changes with three action options
 *
 * Created for form creation flow to prevent accidental data loss.
 * Provides three options: save as draft, discard changes, or continue editing.
 *
 * Updates:
 * - Added icons to all buttons for better visual recognition
 * - Vertical button layout for optimal hierarchy and symmetry
 * - Uses size="md" for better touch targets and visual comfort in modal context
 * - Uses danger-outline variant for destructive action (clear but not aggressive)
 * - Enhanced warning message with consequence reminder
 */

import { AlertTriangle, Save, ArrowLeft, LogOut } from 'lucide-react';
import { Modal } from './Modal';
import Button from './Button';

interface UnsavedChangesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveDraft: () => void;
  onDiscard: () => void;
  isSaving?: boolean;
  canSaveDraft?: boolean;
}

export function UnsavedChangesModal({
  isOpen,
  onClose,
  onSaveDraft,
  onDiscard,
  isSaving = false,
  canSaveDraft = true,
}: UnsavedChangesModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Modifications non sauvegardées" size="sm">
      <div className="space-y-6">
        <div className="flex items-start gap-4">
          <div className="bg-warning-100 rounded-xl p-3 flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-warning-600" />
          </div>
          <div className="flex-1 pt-1">
            <p className="text-base text-neutral-700 leading-relaxed mb-2">
              Vous avez des modifications non sauvegardées. Que souhaitez-vous faire ?
            </p>
            <p className="text-sm text-neutral-600 leading-relaxed">
              Votre travail sera perdu si vous quittez maintenant.
            </p>
            {!canSaveDraft && (
              <p className="text-sm text-neutral-500 mt-2">
                Pour enregistrer comme brouillon, vous devez au minimum renseigner le nom du formulaire.
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <Button
            variant="primary"
            size="md"
            onClick={onSaveDraft}
            disabled={!canSaveDraft || isSaving}
            loading={isSaving}
            fullWidth
            className="gap-2"
          >
            <Save className="w-4 h-4" />
            Enregistrer comme brouillon
          </Button>
          <Button
            variant="secondary"
            size="md"
            onClick={onClose}
            disabled={isSaving}
            fullWidth
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Continuer l'édition
          </Button>
          <Button
            variant="danger-outline"
            size="md"
            onClick={onDiscard}
            disabled={isSaving}
            fullWidth
            className="gap-2"
          >
            <LogOut className="w-4 h-4" />
            Quitter sans sauvegarder
          </Button>
        </div>
      </div>
    </Modal>
  );
}
